/**
 * The rules behind the "generate this announcement" control, with no React in
 * them.
 *
 * Four small questions that all have wrong answers worth avoiding:
 *
 *  - **Does the recording still say what the field says?** A synthesised clip
 *    remembers its script, so this is an exact comparison rather than a guess -
 *    but it has to be made against the text the operator is looking at, not the
 *    one that was saved. Edit the wording and the warning appears on the
 *    keystroke; that is the whole point of comparing here rather than trusting
 *    the server's `stale`, which can only see the last save.
 *  - **How long does it play?** Seconds with one decimal, because the number
 *    that matters is the difference between 8 and 10 seconds of a stranger
 *    waiting, and "00:08" hides it.
 *  - **Which voices are worth offering first?** Estonian ones. Both services
 *    also speak languages nobody proofread a company announcement in.
 *  - **Which voice does the picker open on?** A valid one. This is the question
 *    the provider switch added: a clip made by "peeter" is still described
 *    honestly after somebody moves to ElevenLabs, but "peeter" is not something
 *    that provider can be asked to speak, so the picker must not open on it.
 *
 * NOTHING HERE BRANCHES ON THE PROVIDER, and that is deliberate. A voice is an
 * opaque id with a label and some language codes, whoever offers it; the one
 * place the difference shows is that the two services state languages in
 * different code sets, which `speaksEstonian` absorbs. The provider tag on a
 * voice is carried through to the menu as a HEADING and nothing more - the list
 * is one list, and which service speaks a clip follows from the voice that was
 * picked rather than from a switch the operator has to find first.
 */
import type { NoticeClipInfo, NoticeVariant, TtsVoice, TtsVoiceListing } from "../../../../lib/calls";

/**
 * Estonian in both vocabularies.
 *
 * TartuNLP labels its speakers with ISO 639-3 ("est") and ElevenLabs verifies
 * its voices in ISO 639-1 ("et"). Matching both here rather than normalising the
 * lists is the cheaper honesty: a translation table between two code sets is a
 * thing that goes stale silently and starts hiding voices.
 */
const ESTONIAN_CODES = new Set(["est", "et"]);

export function speaksEstonian(voice: TtsVoice): boolean {
  return voice.languages.some((code) => ESTONIAN_CODES.has(code.trim().toLowerCase()));
}

/**
 * The service's own id, with the provider prefix taken back off.
 *
 * For the one field that stores a voice inside a group that already names its
 * service: the Transcription tab's default ElevenLabs voice, which is an
 * `elevenlabs.voiceId` and would be repeating itself as `elevenlabs:...`.
 * Everywhere else the prefixed form is the value - it is what tells `speak`
 * which service to ask.
 *
 * Cut using the voice's OWN provider tag rather than by looking for a colon: an
 * ElevenLabs id may legitimately contain one, and a split at the first colon
 * would quietly hand the synthesis endpoint half an id.
 */
export function bareVoiceId(voice: TtsVoice): string {
  const prefix = `${voice.provider}:`;
  return voice.id.startsWith(prefix) ? voice.id.slice(prefix.length) : voice.id;
}

/**
 * The clip the field currently points at, whether it was made a minute ago in
 * this form or a month ago by somebody else.
 *
 * `fresh` is what this page just generated and has not saved yet, so the server
 * knows nothing about it; `saved` is what the last load reported. Matching on
 * the address rather than on which is newer is what keeps the description
 * honest after an undo: put the previous URL back and the previous clip is
 * described again.
 */
export function clipForUrl(
  audioUrl: string,
  saved: NoticeClipInfo | undefined,
  fresh: NoticeClipInfo | null,
): NoticeClipInfo | null {
  const url = audioUrl.trim();
  if (!url) return null;
  if (fresh && generatedUrlFor(fresh.id) === url) return fresh;
  if (saved && generatedUrlFor(saved.id) === url) return saved;
  return null;
}

/** Mirrors the API's `generatedAudioUrl`, used only to match a row to a field. */
export function generatedUrlFor(clipId: string): string {
  return `/api/telephony/clip/${clipId}.mp3`;
}

/**
 * The recording speaks a different sentence from the one on screen.
 *
 * Trimmed on both sides because that is what the server stores, so a trailing
 * newline is not a reason to tell somebody their announcement is out of date.
 */
export function clipIsStale(clip: NoticeClipInfo | null, text: string): boolean {
  return clip !== null && clip.text.trim() !== text.trim();
}

/** Nothing to synthesise: an empty script is not an announcement. */
export function canGenerate(variant: NoticeVariant): boolean {
  return variant.text.trim().length > 0;
}

/**
 * Estonian speakers first, then everything else, each run alphabetical.
 *
 * Not a filter. A voice the service offers and this list hid would be a feature
 * removed by a sort function - and on the ElevenLabs side it would hide most of
 * the library, because a stock voice usually carries no verified language at
 * all. Those sort after the verified Estonian ones and stay selectable.
 *
 * ⚠ THE PROVIDER'S OWN ORDER IS PRESERVED ACROSS GROUPS AND THAT IS LOAD-BEARING.
 * The API returns the chosen service's voices first, and a sort that mixed the
 * two would bury the free Tartu speakers somewhere inside sixty ElevenLabs
 * names. So this is a stable sort WITHIN each provider's run, and the runs stay
 * where the API put them.
 */
export function sortVoices(voices: readonly TtsVoice[]): TtsVoice[] {
  const order = [...new Set(voices.map((voice) => voice.provider))];
  return [...voices].sort((a, b) => {
    if (a.provider !== b.provider) return order.indexOf(a.provider) - order.indexOf(b.provider);
    const aEst = speaksEstonian(a);
    const bEst = speaksEstonian(b);
    if (aEst !== bEst) return aEst ? -1 : 1;
    return a.label.localeCompare(b.label, "et");
  });
}

export interface VoiceChoice {
  value: string;
  label: string;
  /** Which service speaks it - the menu draws a heading per run of these. */
  group?: string;
}

/**
 * The speaker list as menu entries, with the chosen one guaranteed to be in it.
 *
 * That guarantee is the whole reason this is a function rather than a `.map()`.
 * A picker shows the option matching its value, so a saved voice the list has
 * not arrived with yet, or one the service has since dropped, would leave the
 * field naming nothing while the button used it anyway. Keeping it in the list
 * makes the field honest without pretending the service still offers it.
 *
 * A voice with languages that do not include Estonian carries them in the label,
 * because "Sulev" and "Sulev (vro)" are the same name and very much not the same
 * announcement. A voice with NO stated language gets no annotation - an empty
 * list is an absent claim, not a claim of absence, and most of the ElevenLabs
 * library is in that state.
 *
 * `groupLabel` turns a provider name into the heading its run is shown under.
 * Passed in rather than looked up here, because the names are translated copy
 * and this file has no business holding any.
 */
export function voiceOptions(
  voices: readonly TtsVoice[],
  current: string,
  groupLabel?: (provider: TtsVoice["provider"]) => string,
): VoiceChoice[] {
  // Only worth a heading when there are two runs to tell apart. One service
  // reachable and a heading over every row is a menu explaining itself to
  // nobody - it is the only answer there is.
  const grouped = groupLabel && new Set(voices.map((voice) => voice.provider)).size > 1;
  const options = sortVoices(voices).map((voice) => ({
    value: voice.id,
    label:
      voice.languages.length === 0 || speaksEstonian(voice)
        ? voice.label
        : `${voice.label} (${voice.languages.join(", ")})`,
    ...(grouped ? { group: groupLabel(voice.provider) } : {}),
  }));
  if (current && !options.some((option) => option.value === current)) {
    options.unshift({ value: current, label: voiceLabelIn(voices, current) });
  }
  return options;
}

/**
 * A voice id as a person reads it, resolved against the list in hand.
 *
 * The stored value is opaque - a TartuNLP first name is readable and an
 * ElevenLabs id is twenty random characters - so the list is the only thing that
 * can turn one into a name. Falling back to the raw value is the honest answer
 * for a voice no service currently offers: it says what the clip records rather
 * than inventing a name for it.
 */
export function voiceLabelIn(voices: readonly TtsVoice[], id: string): string {
  const match = voices.find((voice) => voice.id === id);
  return match ? match.label : id;
}

/**
 * The stored value as an id the CURRENT list holds, or "" when it holds none.
 *
 * Two things it has to absorb, and neither is an error state:
 *
 *  - A clip generated before voices carried their provider holds a bare id
 *    ("peeter"). The list now says `tartunlp:peeter`, so the same voice would
 *    read as missing on the first load after the upgrade, and every picker would
 *    jump off the voice its announcement is actually recorded in. Prefixing the
 *    bare value with the account's chosen provider is the same reading the API
 *    gives it (`voiceRef.ts`), so the two sides agree about what an old row means.
 *  - A voice that has genuinely gone - deleted in an ElevenLabs workspace, or
 *    belonging to a service whose key was removed. That resolves to "" and the
 *    caller falls back, rather than the picker opening on an id the synthesis
 *    endpoint answers 422 to.
 */
export function resolveVoiceId(saved: string | undefined, listing: TtsVoiceListing | null): string {
  const stored = (saved ?? "").trim();
  if (!stored || !listing) return "";
  if (listing.voices.some((voice) => voice.id === stored)) return stored;
  const legacy = `${listing.provider}:${stored}`;
  return listing.voices.some((voice) => voice.id === legacy) ? legacy : "";
}

/**
 * Which voice the picker opens on.
 *
 * The saved clip's voice when some service still offers it, because the useful
 * default is "the voice this announcement already speaks in". Otherwise the
 * listing's own default - which for TartuNLP is the speaker the shipped
 * recordings were made in, and for ElevenLabs is the operator's configured
 * default voice or the first the workspace lists.
 *
 * The fallback matters more than it looks. Without it, an installation whose
 * ElevenLabs key is removed opens every picker on an id nothing can speak, and
 * the generate button answers 400 on a value the operator can see in the field
 * and has no reason to distrust.
 */
export function initialVoice(saved: string | undefined, listing: TtsVoiceListing | null): string {
  if (!listing) return (saved ?? "").trim();
  return resolveVoiceId(saved, listing) || listing.defaultVoice;
}

/**
 * How long the clip plays, in seconds.
 *
 * Null stays null rather than becoming "0.0 s": the duration is measured from
 * the synthesised audio, and a clip whose length could not be read must not be
 * described as instant.
 */
export function clipSeconds(durationMs: number | null): string | null {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0) return null;
  return `${(durationMs / 1000).toFixed(1)} s`;
}
